import React, { useEffect, useState } from 'react';
import { fetchBriefingData, createShareLink, type BriefingData } from '../lib/api';
import { BriefingV2Content } from './briefing-v2/BriefingV2Content';
import { DDChecklistV2 } from './briefing-v2/DDChecklistV2';

interface SiteBriefingV2Props {
  parcelId: string;
  token?: string;
}

// Memo/Essentialist-style briefing. Uses the same /api/briefing/:parcelId endpoint
// as the v1 (SiteBriefing.tsx) — no backend changes. Every number on the page is
// derived from the live API response. Narrative phrasing is templated around real
// data; prose structure never invents numbers.
export const SiteBriefingV2: React.FC<SiteBriefingV2Props> = ({ parcelId, token }) => {
  const [data, setData] = useState<BriefingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareModal, setShareModal] = useState<{ url: string; token: string; redaction?: 'partner' | null } | null>(null);
  const [shareLabelPrompt, setShareLabelPrompt] = useState(false);
  const [shareLabel, setShareLabel] = useState('');
  const [sharePartnerRedaction, setSharePartnerRedaction] = useState(false);
  const [shareCreating, setShareCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'brief' | 'checklist'>('brief');

  // Carve-out disambiguation is opt-in via ?carveOut=<slug> (e.g. a Crown Pine
  // sub-tract that overlaps its full block + an Integra superset). Absent → the
  // fetch URL is byte-identical to the default briefing.
  //
  // The grid lens is a v1-analytical-briefing surface only (single grid
  // surface by design) — the memo view always fetches the default briefing.
  const carveOut = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('carveOut') || undefined)
    : undefined;

  // Partner-redacted editions are proven ONLY for the Crown Pine A1 carve-out — see
  // docs/research/crownpine-siting-dossier/a1-briefing-canon.md §4. The server enforces
  // this independently; this gate only decides whether the checkbox renders at all.
  const isA1Redactable = parcelId === '912d47ae-b055-4716-9375-7946623b7f3b' && carveOut === 'crownpine-blk-194-a1';

  useEffect(() => {
    setLoading(true);
    fetchBriefingData(parcelId, token, undefined, carveOut)
      .then(setData)
      .catch((e: { response?: { status?: number } }) =>
        setError(e?.response?.status === 410 ? 'This share link has expired.' : 'Site not found.'),
      )
      .finally(() => setLoading(false));
  }, [parcelId, token, carveOut]);

  const handleShare = () => {
    setShareLabel('');
    setSharePartnerRedaction(false);
    setShareLabelPrompt(true);
  };

  const handleShareCreate = async () => {
    try {
      setShareCreating(true);
      setShareError(null);
      // Server persists the carve-out lens and returns the URL with ?carveOut=
      // already applied (admin Shares archive shows the exact link sent).
      const layoutsAlt = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('layouts') === 'alt';
      // The seller-alternatives edition is stored on the token row (migration 183);
      // the server returns the URL with &layouts=alt applied. Never on a partner mint.
      const result = await createShareLink(
        parcelId, 30, shareLabel || undefined, carveOut || undefined, undefined,
        isA1Redactable && sharePartnerRedaction ? 'partner' : undefined,
        layoutsAlt && !(isA1Redactable && sharePartnerRedaction) ? 'alt' : undefined,
      );
      // A partner-redacted link is a v1-with-map edition — the memo is not part
      // of that document, so hand back the analytical URL rather than a /v2 one
      // that would only land the recipient on the "this edition is the site
      // brief" notice.
      const shareUrl = result.redaction === 'partner'
        ? result.url
        : result.url.replace(`/briefing/${parcelId}?`, `/briefing/${parcelId}/v2?`);
      setShareLabelPrompt(false);
      setShareModal({ url: shareUrl, token: result.token, redaction: result.redaction });
      setCopied(false);
    } catch {
      setShareError('Failed to create share link.');
      setTimeout(() => setShareError(null), 4000);
    } finally {
      setShareCreating(false);
    }
  };

  const handleCopy = () => {
    if (shareModal) {
      navigator.clipboard.writeText(shareModal.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="v2-status">
        <div className="v2-status-inner">Loading briefing…</div>
        <style>{GOOGLE_FONTS_IMPORT + V2_STYLES}</style>
      </div>
    );
  }
  if (error) {
    return (
      <div className="v2-status">
        <div className="v2-status-inner v2-error">{error}</div>
        <style>{GOOGLE_FONTS_IMPORT + V2_STYLES}</style>
      </div>
    );
  }
  if (!data) return null;

  // Curator gate (structured_intel.suppress_memo_view): the memo edition is not
  // yet optimized for this briefing — bounce a direct /v2 URL to the analytical
  // brief, preserving token/carveOut params. Cinco Frio carries the key.
  if ((data.parcel as any)?.structured_intel?.suppress_memo_view) {
    if (typeof window !== 'undefined') {
      window.location.replace(window.location.pathname.replace(/\/v2\/?$/, '') + window.location.search);
    }
    return null;
  }

  // The partner-redacted edition is v1-with-map only — the investor memo is not
  // part of that document. A direct /v2?token=<partner> URL lands here and gets
  // a notice plus the link to the brief it is entitled to; no memo markup is
  // rendered at all. Presentation policy: the payload is already redacted
  // server-side, so nothing is withheld by this branch that the memo would show.
  if (data.redacted === 'partner') {
    const q = new URLSearchParams();
    if (token) q.set('token', token);
    if (carveOut) q.set('carveOut', carveOut);
    const qs = q.toString();
    return (
      <div className="v2-status">
        <div className="v2-status-inner v2-status-notice">
          <div>This edition is the site brief.</div>
          <a className="v2-status-link" href={`/briefing/${parcelId}${qs ? `?${qs}` : ''}`}>Open the site brief &rarr;</a>
        </div>
        <style>{GOOGLE_FONTS_IMPORT + V2_STYLES}</style>
      </div>
    );
  }

  return (
    <div className="v2-root">
      <div className="v2-toolbar">
        <div className="v2-toolbar-left">
          <div className="v2-toolbar-brand">
            <img src="/atwater-logo-dark.svg" alt="Atwater Energy" className="v2-toolbar-logo" />
          </div>
          <span className="v2-toolbar-sep">·</span>
          <button
            className={`v2-toolbar-toggle ${activeView === 'brief' ? 'active' : ''}`}
            onClick={() => { setActiveView('brief'); window.scrollTo(0, 0); }}
          >Brief</button>
          <span className="v2-toolbar-divider" />
          <button
            className={`v2-toolbar-toggle ${activeView === 'checklist' ? 'active' : ''}`}
            onClick={() => { setActiveView('checklist'); window.scrollTo(0, 0); }}
          >Checklist</button>
        </div>
        <div className="v2-toolbar-right">
          {!token && <button className="v2-toolbar-btn" onClick={handleShare}>Share</button>}
          <button className="v2-toolbar-btn" onClick={() => window.print()}>Print</button>
          <a
            className="v2-toolbar-btn v2-toolbar-btn-view"
            href={(() => {
              // Carry the active carve-out across the view switch — without it a
              // carve-out briefing (e.g. Crown Pine A1) silently reverts to the
              // full-assembly default view. No-op when no carveOut param is present.
              const q = new URLSearchParams();
              if (token) q.set('token', token);
              if (carveOut) q.set('carveOut', carveOut);
              // Seller-alternatives edition flag (Crown Pine A1 Alt link) rides along too.
              if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('layouts') === 'alt') q.set('layouts', 'alt');
              const qs = q.toString();
              return `/briefing/${parcelId}${qs ? `?${qs}` : ''}`;
            })()}
            title="Switch to the analytical brief"
          >
            Analytical View <span className="v2-arrow">&rarr;</span>
          </a>
        </div>
      </div>
      {activeView === 'brief'
        ? <BriefingV2Content data={data} />
        : <DDChecklistV2 data={data} />
      }

      {shareError && (
        <div className="v2-share-toast v2-share-toast-error">{shareError}</div>
      )}

      {shareLabelPrompt && (
        <div className="v2-modal-overlay">
          <div className="v2-modal">
            <div className="v2-modal-title">Name this share link</div>
            <div className="v2-modal-subtitle">Optional — helps you track who viewed it in the admin panel.</div>
            <input
              className="v2-modal-input"
              value={shareLabel}
              onChange={e => setShareLabel(e.target.value)}
              placeholder='e.g. "Taylor County site brief"'
              maxLength={100}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleShareCreate()}
            />
            {isA1Redactable && (
              <label className="v2-modal-checkbox-row">
                <input
                  type="checkbox"
                  checked={sharePartnerRedaction}
                  onChange={e => setSharePartnerRedaction(e.target.checked)}
                />
                <span>
                  <div className="v2-modal-checkbox-label">Partner-redacted edition</div>
                  <div className="v2-modal-checkbox-caption">Strips pricing &amp; client identity — for counterparty viewing</div>
                </span>
              </label>
            )}
            <div className="v2-modal-actions">
              <button className="v2-modal-btn v2-modal-btn-primary" onClick={handleShareCreate} disabled={shareCreating}>
                {shareCreating ? 'Creating...' : 'Create Link'}
              </button>
              <button className="v2-modal-btn v2-modal-btn-secondary" onClick={() => setShareLabelPrompt(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {shareModal && (
        <div className="v2-modal-overlay">
          <div className="v2-modal">
            <div className="v2-modal-title-row">
              <div className="v2-modal-title">Share Briefing</div>
              {shareModal.redaction === 'partner' && (
                <span className="v2-modal-badge">PARTNER-REDACTED</span>
              )}
            </div>
            <input
              className="v2-modal-input v2-modal-input-mono"
              value={shareModal.url}
              readOnly
              onClick={e => (e.target as HTMLInputElement).select()}
            />
            <div className="v2-modal-actions">
              <button className="v2-modal-btn v2-modal-btn-primary" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              <button className="v2-modal-btn v2-modal-btn-secondary" onClick={() => setShareModal(null)}>Close</button>
            </div>
            <div className="v2-modal-hint">Expires in 30 days. Anyone with this link can view the briefing.</div>
          </div>
        </div>
      )}

      <style>{GOOGLE_FONTS_IMPORT + V2_STYLES}</style>
    </div>
  );
};

// ---------- styles ----------------------------------------------------------

const GOOGLE_FONTS_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,opsz,wght@0,8..60,400..700;1,8..60,400..700&family=Inter:ital,wght@0,400..700;1,400..700&display=swap');
`;

const V2_STYLES = `
.v2-root {
  --paper:       #F2EDE1;
  --paper-deep:  #EAE3D2;
  --paper-edge:  #DDD4BE;
  --ink:         #17140E;
  --ink-body:    #3A352B;
  --ink-muted:   #6E665A;
  --ink-soft:    #948B7D;
  --rule:        rgba(23,20,14,0.10);
  --rule-hard:   rgba(23,20,14,0.28);
  --pass:        #506B2E;
  --pass-wash:   rgba(80,107,46,0.10);
  --caution:     #A17620;
  --caution-wash:rgba(161,118,32,0.10);
  --fail:        #8C3626;
  --fail-wash:   rgba(140,54,38,0.10);
  --accent:      #B5472B;
  --v2-serif:    "Source Serif 4", "Source Serif Pro", Georgia, "Times New Roman", serif;
  --v2-sans:     "Inter", -apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif;
  --v2-mono:     ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  /* Type scale — 8 stops */
  --ts-display:  64px;
  --ts-stat:     40px;
  --ts-heading:  28px;
  --ts-subhead:  20px;
  --ts-body:     16px;
  --ts-small:    14px;
  --ts-caption:  12px;
  --ts-micro:    10px;
  background: var(--paper-edge);
  min-height: 100vh;
  padding: 20px 20px 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: var(--v2-sans);
  color: var(--ink-body);
  font-size: var(--ts-small);
  line-height: 1.55;
}

.v2-status { min-height: 100vh; background: #DDD4BE; display: flex; align-items: center; justify-content: center; font-family: "Inter", sans-serif; color: #6E665A; }
.v2-status-inner { font-size: var(--ts-small); letter-spacing: 0.04em; text-transform: uppercase; }
.v2-status-inner.v2-error { color: #8C3626; }
/* Scoped to the notice, not to .v2-status-inner: the loading and error screens
   of every /v2 page share that class and must stay exactly as they were. */
.v2-status-inner.v2-status-notice { text-align: center; }
.v2-status-link { display: inline-block; margin-top: 14px; color: #B5472B; text-decoration: none; border-bottom: 1px solid #B5472B; letter-spacing: 0.06em; }

.v2-toolbar {
  width: 960px; max-width: 100%;
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 8px 18px;
  font-family: var(--v2-mono);
  font-size: var(--ts-micro);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-muted);
}
.v2-toolbar-left { display: flex; align-items: center; gap: 14px; }
.v2-toolbar-brand { display: flex; align-items: center; gap: 12px; }
.v2-toolbar-logo { height: 26px; width: auto; display: block; }
.v2-toolbar-sep { color: var(--ink-soft); }
.v2-toolbar-right { display: flex; gap: 8px; }
.v2-toolbar-btn {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.12em; text-transform: uppercase;
  padding: 6px 12px; background: var(--paper); color: var(--ink);
  border: 1px solid var(--rule-hard); border-radius: 2px; cursor: pointer;
  text-decoration: none;
}
.v2-toolbar-btn:hover { background: var(--ink); color: var(--paper); border-color: var(--ink); }
.v2-toolbar-btn-view { color: var(--accent); border-color: var(--accent); }
.v2-toolbar-btn-view:hover { background: var(--accent); color: var(--paper); border-color: var(--accent); }
.v2-toolbar-btn-view .v2-arrow { display: inline-block; margin-left: 4px; transition: transform 0.15s; }
.v2-toolbar-btn-view:hover .v2-arrow { transform: translateX(2px); }

.v2-share-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 8px 16px; border-radius: 2px; font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.06em; text-transform: uppercase; z-index: 110; white-space: nowrap; }
.v2-share-toast-error { background: var(--fail); color: var(--paper); }

.v2-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(23,20,14,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
.v2-modal { background: var(--paper); border-radius: 4px; padding: 28px; max-width: 440px; width: 90%; font-family: var(--v2-sans); box-shadow: 0 18px 48px -24px rgba(23,20,14,0.28); }
.v2-modal-title { font-family: var(--v2-serif); font-size: var(--ts-subhead); font-weight: 600; color: var(--ink); margin-bottom: 4px; }
.v2-modal-title-row { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.v2-modal-title-row .v2-modal-title { margin-bottom: 0; }
.v2-modal-badge { font-family: var(--v2-mono); font-size: 9px; font-weight: 700; letter-spacing: 0.06em; color: var(--paper); background: var(--ink); padding: 2px 6px; border-radius: 2px; }
.v2-modal-subtitle { font-size: var(--ts-caption); color: var(--ink-muted); margin-bottom: 14px; }
.v2-modal-checkbox-row { display: flex; align-items: flex-start; gap: 8px; margin-top: 14px; cursor: pointer; }
.v2-modal-checkbox-row input[type="checkbox"] { margin-top: 2px; }
.v2-modal-checkbox-label { font-size: var(--ts-caption); font-weight: 600; color: var(--ink); }
.v2-modal-checkbox-caption { font-size: var(--ts-micro); color: var(--ink-muted); margin-top: 2px; }
.v2-modal-input { width: 100%; padding: 8px 12px; font-size: var(--ts-small); font-family: var(--v2-sans); border: 1px solid var(--rule-hard); border-radius: 2px; box-sizing: border-box; background: var(--paper-deep); color: var(--ink-body); }
.v2-modal-input:focus { outline: none; border-color: var(--accent); }
.v2-modal-input-mono { font-family: var(--v2-mono); font-size: var(--ts-caption); }
.v2-modal-actions { display: flex; gap: 8px; margin-top: 14px; }
.v2-modal-btn { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.12em; text-transform: uppercase; padding: 8px 16px; border-radius: 2px; cursor: pointer; border: none; }
.v2-modal-btn:disabled { opacity: 0.5; cursor: default; }
.v2-modal-btn-primary { background: var(--ink); color: var(--paper); }
.v2-modal-btn-primary:hover:not(:disabled) { background: var(--accent); }
.v2-modal-btn-secondary { background: var(--paper-deep); color: var(--ink-body); border: 1px solid var(--rule-hard); }
.v2-modal-btn-secondary:hover { background: var(--paper-edge); }
.v2-modal-hint { font-size: var(--ts-micro); color: var(--ink-soft); margin-top: 10px; letter-spacing: 0.02em; }

.v2-sheet {
  position: relative;
  width: 960px; max-width: 100%;
  background: var(--paper);
  box-shadow: 0 1px 0 rgba(23,20,14,0.04), 0 18px 48px -24px rgba(23,20,14,0.28), 0 40px 80px -40px rgba(23,20,14,0.18);
  background-image:
    radial-gradient(circle at 15% 12%, rgba(23,20,14,0.018) 0, transparent 50%),
    radial-gradient(circle at 85% 78%, rgba(23,20,14,0.022) 0, transparent 60%),
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.09   0 0 0 0 0.08   0 0 0 0 0.055  0 0 0 0.035 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
  background-blend-mode: multiply;
}
.v2-sheet::before {
  content: ""; position: absolute; top: 0; bottom: 0; left: 72px; width: 1px;
  background: linear-gradient(to bottom, transparent 0, var(--rule) 48px, var(--rule) calc(100% - 48px), transparent 100%);
  pointer-events: none;
}

.v2-dateline {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.08em;
  color: var(--ink); padding: 22px 56px 14px;
  display: flex; justify-content: space-between; align-items: baseline;
  border-bottom: 1px solid var(--ink); text-transform: uppercase;
}
.v2-seq { color: var(--ink-muted); }
.v2-seq strong { color: var(--ink); font-weight: 600; }
.v2-right { color: var(--ink-muted); letter-spacing: 0.10em; }
.v2-right strong { color: var(--fail); font-weight: 600; letter-spacing: 0.14em; }

.v2-masthead { padding: 64px 80px 28px; position: relative; }
.v2-eyebrow {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--ink-muted); margin-bottom: 18px;
}
.v2-eyebrow-sep {
  color: var(--ink-soft); padding: 0 10px; font-weight: 400;
}
.v2-dot {
  display: inline-block; width: 6px; height: 6px; border-radius: 50%;
  background: var(--accent); margin: 0 10px 1px; vertical-align: middle;
}
.v2-masthead h1 {
  font-family: var(--v2-serif); font-optical-sizing: auto;
  font-weight: 700; font-size: var(--ts-display); line-height: 0.98;
  letter-spacing: -0.02em; color: var(--ink); margin: 0 0 16px;
}
.v2-subhead {
  font-family: var(--v2-serif); font-weight: 400;
  font-size: var(--ts-subhead); line-height: 1.4; color: var(--ink);
  max-width: 720px; margin: 0 0 28px;}
.v2-subhead em { font-style: normal; }
.v2-qa {
  border-top: 1px solid var(--rule); border-bottom: 1px solid var(--rule);
  padding: 18px 0; display: grid; grid-template-columns: 180px 1fr;
  column-gap: 32px; row-gap: 10px; max-width: 820px; margin: 0;
}
.v2-qa dt {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--ink-muted); padding-top: 3px;
}
.v2-qa dd {
  font-family: var(--v2-serif); font-size: var(--ts-body); line-height: 1.45;
  color: var(--ink); margin: 0;
}
.v2-qa dd strong { color: var(--accent); font-weight: 500; }

.v2-numbers {
  --v2-stat-cell-w: 220px;
  display: grid; grid-template-columns: minmax(0, 1fr) var(--v2-stat-cell-w) var(--v2-stat-cell-w);
  border-top: 1px solid var(--ink); border-bottom: 1px solid var(--ink); margin: 0 56px;
}
.v2-num { padding: 26px 24px 22px; border-right: 1px solid var(--rule); position: relative; }
.v2-num:not(.v2-num-hero) .v2-label { white-space: nowrap; }
.v2-num:last-child { border-right: none; }
.v2-label { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.16em; text-transform: uppercase; color: var(--ink-muted); margin-bottom: 10px; }
.v2-value { font-family: var(--v2-serif); font-weight: 600; font-size: var(--ts-heading); line-height: 1; color: var(--ink); letter-spacing: -0.02em; }
.v2-unit { font-size: inherit; color: inherit; font-weight: inherit; }
.v2-delta { margin-top: 10px; font-size: var(--ts-caption); color: var(--ink-muted); line-height: 1.4; }
.v2-num-hero { padding: 30px 28px 26px; }
.v2-num-hero .v2-value { font-size: 60px; }
.v2-num-hero .v2-unit { font-size: inherit; }
.v2-num-hero .v2-delta { margin-top: 12px; font-size: var(--ts-small); }

/* Power Story: multi-line phased value */
.v2-num-hero .v2-value-stack {
  display: flex; flex-direction: column; gap: 0;
  font-family: var(--v2-serif); line-height: 1.05;
  letter-spacing: -0.02em;
}
.v2-num-hero .v2-value-line { display: block; line-height: 1.05; }
.v2-num-hero .v2-value-stage {
  font-size: 48px; font-weight: 700; color: var(--ink);
}
.v2-num-hero .v2-value-when {
  font-size: 24px; font-weight: 400; color: var(--ink-muted);
}
.v2-num-hero .v2-value-arrow {
  font-size: 32px; font-weight: 400; color: var(--ink-soft);
}
.v2-num-hero .v2-delta-prose {
  margin-top: 18px;
  font-family: var(--v2-serif); font-style: normal;
  font-size: var(--ts-small); line-height: 1.55;
  color: var(--ink-body); max-width: 56ch;
}
.v2-num-hero .v2-delta-prose strong { color: var(--ink); font-weight: 600; }

.v2-sheet section { padding: 0 80px; scroll-margin-top: 20px; }
.v2-sheet .v2-numbers + section { margin-top: 20px; }
.v2-sheet section + section { margin-top: 64px; }

.v2-section-head {
  display: flex; align-items: baseline; justify-content: space-between;
  margin-bottom: 20px; padding-bottom: 10px; border-bottom: 1px solid var(--rule);
}
.v2-num-tag { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.18em; color: var(--ink-muted); text-transform: uppercase; }
.v2-section-head h2 {
  font-family: var(--v2-serif); font-weight: 600;
  font-size: var(--ts-heading); line-height: 1; color: var(--ink);
  letter-spacing: -0.015em; flex: 1; margin: 0 28px;
}
.v2-meta { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.12em; text-transform: uppercase; color: var(--ink-muted); }

.v2-prose {
  font-family: var(--v2-serif);  font-size: var(--ts-body); line-height: 1.62; color: var(--ink); max-width: 70ch; margin: 0;
}
.v2-prose + .v2-prose { margin-top: 14px; }
.v2-prose strong { font-weight: 600; color: var(--ink); background: linear-gradient(180deg, transparent 62%, rgba(181,71,43,0.06) 62%); padding: 0 1px; }
.v2-prose em { font-style: italic; color: var(--ink); }

.v2-precedent { display: grid; grid-template-columns: 260px 1fr; column-gap: 40px; align-items: start; }
.v2-precedent-data { border-left: 2px solid var(--ink); padding: 4px 0 4px 20px; }
.v2-pd-label { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.16em; color: var(--ink-muted); text-transform: uppercase; margin-top: 10px; }
.v2-pd-label:first-child { margin-top: 0; }
.v2-pd-val { font-family: var(--v2-serif); font-size: var(--ts-body); line-height: 1.3; color: var(--ink); margin-top: 2px; }
.v2-pd-val.v2-pd-small { font-size: var(--ts-small); }
.v2-pd-val em { color: var(--ink-muted); font-style: italic; }

.v2-gates { display: grid; grid-template-columns: repeat(2, 1fr); column-gap: 48px; row-gap: 2px; }
.v2-gate { display: grid; grid-template-columns: 112px 1fr auto; column-gap: 18px; align-items: baseline; padding: 14px 0; border-bottom: 1px solid var(--rule); }
.v2-gate:nth-last-child(-n+2) { border-bottom: none; }
.v2-gate-name { font-family: var(--v2-serif); font-size: var(--ts-subhead); font-weight: 600; color: var(--ink); line-height: 1; }
.v2-gate-evidence { font-family: var(--v2-sans); font-size: var(--ts-small); color: var(--ink-muted); line-height: 1.35; }
.v2-badge { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.16em; text-transform: uppercase; padding: 4px 9px; border-radius: 2px; }
.v2-badge.pass    { color: var(--pass);    background: var(--pass-wash); }
.v2-badge.caution { color: var(--caution); background: var(--caution-wash); }
.v2-badge.fail    { color: var(--fail);    background: var(--fail-wash); }
.v2-badge.inquiry { color: var(--ink-muted); background: rgba(23,20,14,0.06); }

.v2-toolbar-toggle {
  cursor: pointer; padding: 4px 8px; border-radius: 2px;
  font-family: var(--v2-mono); font-size: var(--ts-micro);
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-muted); transition: color 0.15s;
  background: none; border: none;
}
.v2-toolbar-toggle:hover { color: var(--ink); }
.v2-toolbar-toggle.active { color: var(--ink); font-weight: 600; }
.v2-toolbar-divider { width: 1px; height: 12px; background: var(--rule-hard); align-self: center; }

.v2-dd-header { padding: 48px 80px 0; }
.v2-dd-title { font-family: var(--v2-serif); font-size: var(--ts-heading); font-weight: 600; color: var(--ink); margin: 0 0 6px; }
.v2-dd-summary { font-family: var(--v2-mono); font-size: var(--ts-caption); letter-spacing: 0.06em; color: var(--ink-muted); }
.v2-dd-summary-inquiry { color: var(--ink); font-weight: 600; }
.v2-dd-summary-flagged { color: var(--caution); font-weight: 600; }
.v2-dd-summary-addressed { color: var(--pass); }

.v2-dd-strip {
  display: grid; grid-template-columns: repeat(12, 1fr);
  margin: 24px 80px 0; border: 1px solid var(--rule-hard); border-radius: 2px; overflow: hidden;
}
.v2-dd-strip-cell {
  text-align: center; padding: 10px 2px 8px;
  border-right: 1px solid var(--rule); position: relative;
}
.v2-dd-strip-cell:last-child { border-right: none; }
.v2-dd-strip-cell::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px; }
.v2-dd-strip-cell.status-pass   { background: var(--pass-wash); }
.v2-dd-strip-cell.status-pass::before   { background: var(--pass); }
.v2-dd-strip-cell.status-caution { background: var(--caution-wash); }
.v2-dd-strip-cell.status-caution::before { background: var(--caution); }
.v2-dd-strip-cell.status-inquiry { background: var(--paper-deep); }
.v2-dd-strip-cell.status-inquiry::before { background: var(--ink-muted); }
.v2-dd-abbrev { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-muted); line-height: 1; }
.v2-dd-count { font-family: var(--v2-mono); font-size: var(--ts-caption); font-weight: 600; margin-top: 4px; line-height: 1; }
.v2-dd-count.status-pass    { color: var(--pass); }
.v2-dd-count.status-caution { color: var(--caution); }
.v2-dd-count.status-inquiry { color: var(--ink-muted); }

.v2-dd-body { padding: 0 80px; }
.v2-dd-category { margin-top: 48px; }
.v2-dd-category .v2-section-head { margin-bottom: 0; }
.v2-dd-rows { border-top: 1px solid var(--rule); }
.v2-dd-row {
  display: grid; grid-template-columns: 35% 95px 1fr; column-gap: 12px;
  align-items: baseline; padding: 10px 0;
  border-bottom: 1px solid var(--rule);
}
.v2-dd-row:hover { background: var(--paper-deep); margin: 0 -12px; padding: 10px 12px; }
.v2-dd-item-name { font-family: var(--v2-serif); font-size: var(--ts-small); color: var(--ink); }
.v2-dd-item-summary { font-family: var(--v2-sans); font-size: var(--ts-small); color: var(--ink-body); line-height: 1.5; }

.v2-kill { width: 100%; border-collapse: collapse; font-family: var(--v2-sans); }
.v2-kill th { text-align: left; font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.16em; text-transform: uppercase; color: var(--ink-muted); font-weight: 500; padding: 0 0 10px; border-bottom: 1px solid var(--ink); }
.v2-kill td { padding: 18px 0; vertical-align: top; border-bottom: 1px solid var(--rule); font-size: var(--ts-small); line-height: 1.45; color: var(--ink); }
.v2-kill td:first-child { font-family: var(--v2-serif); font-size: var(--ts-body); color: var(--ink); padding-right: 32px; width: 30%; }
.v2-kill td:nth-child(2) { color: var(--ink-body); padding-right: 32px; }
.v2-kill td:nth-child(3) { width: 22%; padding-right: 16px; }
.v2-kill td:last-child { width: 18%; text-align: right; }
.v2-cost { font-family: var(--v2-mono); font-size: var(--ts-small); color: var(--ink); letter-spacing: -0.01em; }
.v2-when { font-family: var(--v2-mono); font-size: var(--ts-micro); color: var(--ink-muted); letter-spacing: 0.06em; text-transform: uppercase; }
.v2-kill tr:last-child td { border-bottom: none; }

.v2-schematic { background: var(--paper-deep); border: 1px solid var(--rule-hard); padding: 28px; display: grid; grid-template-columns: 1.5fr 1fr; column-gap: 40px; align-items: start; }
.v2-legend { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.04em; color: var(--ink); }
.v2-pin-row { display: grid; grid-template-columns: 22px 1fr auto; column-gap: 12px; padding: 8px 0; border-bottom: 1px solid var(--rule); align-items: baseline; }
.v2-pin-row:last-child { border-bottom: none; }
.v2-pin-mark { font-family: var(--v2-mono); font-weight: 700; font-size: var(--ts-micro); color: var(--ink); text-align: center; }
.v2-pin-label { font-family: var(--v2-sans); font-size: var(--ts-small); color: var(--ink); text-transform: none; letter-spacing: 0; }
.v2-pin-dist { font-family: var(--v2-mono); font-size: var(--ts-micro); color: var(--ink-muted); }
.v2-pin-row-stacked { grid-template-columns: 22px 1fr; }
.v2-pin-row-stacked .v2-pin-dist { grid-column: 2; display: flex; flex-direction: column; gap: 2px; margin-top: 4px; }
.v2-cap { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.16em; text-transform: uppercase; color: var(--ink-muted); margin-top: 18px; padding-top: 12px; border-top: 1px solid var(--rule); line-height: 1.5; }

.v2-schematic-map { width: 100%; height: 360px; position: relative; }
.v2-schematic-map .mapboxgl-ctrl-bottom-left,
.v2-schematic-map .mapboxgl-ctrl-bottom-right { display: none; }
.v2-map-explore { position: absolute; bottom: 8px; right: 8px; font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.12em; text-transform: uppercase; padding: 4px 10px; background: var(--paper); color: var(--ink-muted); border: 1px solid var(--rule-hard); cursor: pointer; z-index: 2; }
.v2-map-explore:hover { background: var(--ink); color: var(--paper); }
.v2-map-pin { width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; font-family: var(--v2-mono); font-size: 10px; font-weight: 700; background: var(--paper); border: 2px solid var(--ink); border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
.v2-map-pin.square { border-radius: 2px; }
.v2-map-pin.accent { border-color: var(--accent); color: var(--accent); }
.v2-map-pin.ok { border-color: var(--pass); color: var(--pass); }
.v2-map-pin.rail { border-color: #A8AFB5; color: #6E665A; }
.v2-map-popup .mapboxgl-popup-content { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.04em; background: var(--paper); color: var(--ink); padding: 6px 10px; border: 1px solid var(--rule-hard); box-shadow: 0 2px 6px rgba(0,0,0,0.12); border-radius: 0; }
.v2-map-popup .mapboxgl-popup-tip { border-top-color: var(--paper); }

.v2-infra-grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 48px; row-gap: 24px; }
.v2-infra-block { padding-left: 18px; border-left: 1px solid var(--rule-hard); }
.v2-infra-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px; }
.v2-infra-block h3 { font-family: var(--v2-serif); font-size: var(--ts-subhead); font-weight: 600; color: var(--ink); line-height: 1; margin: 0; }
.v2-tag { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.14em; text-transform: uppercase; color: var(--pass); }
.v2-tag.amber { color: var(--caution); }
.v2-tag.brick { color: var(--fail); }
.v2-infra-block p { font-size: var(--ts-small); line-height: 1.55; color: var(--ink-body); margin: 0; }
.v2-infra-block p strong { color: var(--ink); font-weight: 500; }

.v2-epist { display: grid; grid-template-columns: repeat(4, 1fr); column-gap: 28px; margin-bottom: 24px; }
.v2-epist-col { padding-top: 16px; border-top: 2px solid var(--ink); }
.v2-col-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 10px; }
.v2-epist-col h4 { font-family: var(--v2-serif); font-size: var(--ts-subhead); font-weight: 600; color: var(--ink); line-height: 1; margin: 0; }
.v2-dot-mark { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.v2-epist-col[data-tier="known"]    .v2-dot-mark { background: var(--pass); }
.v2-epist-col[data-tier="believed"] .v2-dot-mark { background: #7D8F55; }
.v2-epist-col[data-tier="assumed"]  .v2-dot-mark { background: var(--caution); }
.v2-epist-col[data-tier="pending"]  .v2-dot-mark { background: var(--fail); }
.v2-epist-col ul { list-style: none; padding: 0; margin: 0; }
.v2-epist-col li { font-size: var(--ts-small); line-height: 1.5; color: var(--ink-body); padding: 4px 0; border-bottom: 1px dotted var(--rule); }
.v2-epist-col li:last-child { border-bottom: none; }
.v2-mono { font-family: var(--v2-mono); font-size: var(--ts-micro); color: var(--ink-muted); display: block; margin-top: 1px; }
.v2-epist-pull { font-family: var(--v2-serif); font-style: italic; font-size: var(--ts-body); line-height: 1.5; color: var(--ink); max-width: 62ch; border-top: 1px solid var(--rule); padding-top: 20px; margin: 0; }

.v2-decision { background: var(--ink) !important; color: var(--paper); padding: 40px 56px !important; margin-top: 52px; }
.v2-decision .v2-eyebrow { color: rgba(242,237,225,0.6); }
.v2-decision .v2-eyebrow .v2-dot { background: #D88F5E; }
.v2-decision h2 { font-family: var(--v2-serif); font-weight: 600; font-size: var(--ts-heading); color: var(--paper); line-height: 1.1; margin: 6px 0 22px; }
.v2-decision ol { list-style: none; counter-reset: step; border-top: 1px solid rgba(242,237,225,0.18); max-width: 760px; padding: 0; margin: 0; }
.v2-decision ol li { counter-increment: step; padding: 16px 0 16px 48px; border-bottom: 1px solid rgba(242,237,225,0.14); position: relative; font-size: var(--ts-small); line-height: 1.5; color: rgba(242,237,225,0.92); }
.v2-decision ol li::before { content: counter(step, decimal-leading-zero); position: absolute; left: 0; top: 16px; font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.1em; color: #D88F5E; }
.v2-decision ol li strong { color: var(--paper); font-weight: 500; }
.v2-closer { margin-top: 28px; font-family: var(--v2-serif); font-size: var(--ts-subhead); line-height: 1.45; color: var(--paper); font-style: italic; max-width: 60ch; margin-bottom: 0; }
.v2-closer strong { color: #D88F5E; font-style: normal; font-weight: 500; }

.v2-footer { padding: 32px 80px 40px; display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 24px; border-top: 1px solid var(--rule); margin: 0 !important; }
.v2-brand { font-family: var(--v2-serif); font-size: var(--ts-subhead); color: var(--ink); font-weight: 600; }
.v2-brand-logo { height: 44px; width: auto; display: block; margin-bottom: 6px; }
.v2-sources { font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-muted); max-width: 620px; text-align: right; line-height: 1.7; }

/* ---------- AOI / operator-zone briefing additions ---------- */
.v2-zone-context > * + * { margin-top: 24px; }

.v2-zone-callout {
  display: flex; align-items: center; justify-content: space-between;
  gap: 20px; flex-wrap: wrap;
  padding: 16px 22px;
  border-top: 2px solid var(--accent);
  border-bottom: 1px solid var(--rule-hard);
  background: rgba(181, 71, 43, 0.04);
}
.v2-zone-callout-text { flex: 1; min-width: 260px; }
.v2-zone-eyebrow {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--accent); margin-bottom: 6px;
}
.v2-zone-body {
  font-family: var(--v2-serif);  font-size: var(--ts-body); line-height: 1.5; color: var(--ink);
}
.v2-zone-note { color: var(--ink-muted); font-style: italic; }
.v2-zone-link {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.12em;
  text-transform: uppercase; padding: 6px 12px;
  background: var(--paper); color: var(--accent);
  border: 1px solid var(--accent); border-radius: 2px;
  text-decoration: none; white-space: nowrap; flex-shrink: 0;
}
.v2-zone-link:hover { background: var(--accent); color: var(--paper); }
.v2-zone-link .v2-arrow { display: inline-block; margin-left: 4px; transition: transform 0.15s; }
.v2-zone-link:hover .v2-arrow { transform: translateX(2px); }

.v2-carve-out {
  padding: 14px 0 14px 22px;
  border-left: 2px solid var(--accent);
}
.v2-carve-out-eyebrow {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--accent); margin-bottom: 6px;
}
.v2-carve-out-position { color: var(--ink-muted); font-weight: 400; letter-spacing: 0.10em; }
.v2-carve-out-name {
  font-family: var(--v2-serif); font-weight: 600; font-size: var(--ts-subhead); line-height: 1.15;
  color: var(--ink); margin: 0 0 12px;
}
.v2-carve-out-data {
  display: grid; grid-template-columns: max-content 1fr;
  column-gap: 20px; row-gap: 4px; margin-bottom: 12px;
  font-size: var(--ts-small);
}
.v2-carve-out-prose {
  font-family: var(--v2-serif);  font-size: var(--ts-body); line-height: 1.55; color: var(--ink);
  margin: 0 0 10px; max-width: 70ch;
}
.v2-carve-out-note {
  font-family: var(--v2-serif); font-style: italic;
  font-size: var(--ts-small); line-height: 1.5; color: var(--ink-body);
  padding: 10px 14px; background: var(--paper-deep);
  border-left: 3px solid var(--accent); max-width: 70ch;
}

.v2-supply-story { padding-top: 8px; }
.v2-supply-eyebrow {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--ink-muted); margin-bottom: 6px;
}
.v2-supply-title {
  font-family: var(--v2-serif); font-weight: 600; font-size: var(--ts-heading); line-height: 1.05;
  letter-spacing: -0.015em; color: var(--ink); margin: 0 0 16px;
}
.v2-supply-lede {
  font-family: var(--v2-serif);  font-size: var(--ts-body); line-height: 1.6; color: var(--ink);
  max-width: 70ch; margin: 0 0 20px;
}
.v2-supply-lede strong {
  font-weight: 600; color: var(--ink);
  background: linear-gradient(180deg, transparent 62%, rgba(181,71,43,0.06) 62%);
  padding: 0 1px;
}
.v2-supply-lede em { font-style: italic; }

.v2-supply-hero { display: grid; gap: 10px; margin-bottom: 22px; }
.v2-supply-hero[data-cells="1"] { grid-template-columns: minmax(0, 1fr); }
.v2-supply-hero[data-cells="2"] { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.v2-supply-hero[data-cells="3"] { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.v2-supply-cell {
  padding: 12px 14px;
  border: 1px solid var(--rule-hard); background: var(--paper);
}
.v2-supply-cell.emphasis {
  border-color: rgba(181, 71, 43, 0.5);
  background: rgba(181, 71, 43, 0.05);
}
.v2-supply-cell-value {
  font-family: var(--v2-serif); font-weight: 600; font-size: var(--ts-subhead); line-height: 1;
  color: var(--ink); margin-bottom: 6px;
}
.v2-supply-cell.emphasis .v2-supply-cell-value { color: var(--accent); }
.v2-supply-cell-label {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--ink-muted);
}

.v2-supply-block {
  margin-bottom: 12px;
  padding-left: 18px;
  border-left: 1px solid var(--rule-hard);
}
.v2-supply-block-label {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--accent); margin-bottom: 4px;
}
.v2-supply-block-label.muted { color: var(--ink-muted); }
.v2-supply-block-body {
  font-family: var(--v2-serif);  font-size: var(--ts-small); line-height: 1.55; color: var(--ink);
  margin: 0; max-width: 70ch;
}
.v2-supply-drill { margin-top: 10px; padding-left: 18px; border-left: 1px solid var(--rule); }
.v2-supply-drill-body {
  font-family: var(--v2-serif); font-style: italic;
  font-size: var(--ts-small); line-height: 1.55; color: var(--ink-muted);
  margin: 0; max-width: 70ch;
}

/* ---- Site Intelligence box (curated, hand-edited per parcel) ---- */
.v2-site-intel {
  margin: 32px 80px 8px;
  padding: 28px 32px;
  background: linear-gradient(180deg, rgba(181,71,43,0.05) 0%, rgba(181,71,43,0.02) 100%);
  border-top: 3px solid var(--accent);
  border-bottom: 1px solid var(--rule-hard);
}
.v2-site-intel-eyebrow {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.22em;
  text-transform: uppercase; color: var(--accent); margin-bottom: 18px;
  font-weight: 600;
}
.v2-site-intel-block { margin-bottom: 16px; }
.v2-site-intel-block:last-child { margin-bottom: 0; }
.v2-site-intel-heading {
  font-family: var(--v2-mono); font-size: var(--ts-caption); letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--ink); font-weight: 600;
  margin-bottom: 5px;
}
.v2-site-intel-body {
  font-family: var(--v2-serif);  font-size: var(--ts-body); line-height: 1.6; color: var(--ink);
  margin: 0; max-width: 72ch;
}
.v2-site-intel-body strong {
  font-weight: 600; color: var(--ink);
  background: linear-gradient(180deg, transparent 60%, rgba(181,71,43,0.06) 60%);
  padding: 0 1px;
}
.v2-site-intel-body em {
  font-style: italic; color: var(--ink); font-weight: 500;
}

/* ---- Curated Precedent stack (overrides Sec. 02). Highlight uses amber
   eyebrow + amber title only — same border thickness, same paper bg,
   simplicity over decoration. ---- */
.v2-precedent-stack { display: flex; flex-direction: column; gap: 14px; }
.v2-pc-card {
  padding: 16px 20px;
  border: 1px solid var(--rule-hard);
  border-radius: 2px;
  background: var(--paper);
}
.v2-pc-eyebrow {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--ink-muted); margin-bottom: 6px;
}
.v2-pc-card.highlight .v2-pc-eyebrow { color: var(--accent); font-weight: 600; }
.v2-pc-title {
  font-family: var(--v2-serif); font-size: var(--ts-subhead); line-height: 1.15;
  color: var(--ink); font-weight: 600; margin-bottom: 12px;
}
.v2-pc-card.highlight .v2-pc-title { color: var(--accent); }
.v2-pc-stat-row {
  display: grid; grid-template-columns: repeat(4, 1fr);
  column-gap: 16px; row-gap: 8px; margin-bottom: 10px;
  padding: 10px 0; border-top: 1px solid var(--rule); border-bottom: 1px solid var(--rule);
}
.v2-pc-stat { display: flex; flex-direction: column; gap: 2px; }
.v2-pc-stat-val {
  font-family: var(--v2-serif);  font-size: var(--ts-body); color: var(--ink); line-height: 1.2;
}
.v2-pc-stat-val.v2-pc-stat-small { font-size: var(--ts-small); }
.v2-pc-stat-lbl {
  font-family: var(--v2-mono); font-size: var(--ts-micro); letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--ink-muted);
}
.v2-pc-operator {
  font-family: var(--v2-mono); font-size: var(--ts-caption); letter-spacing: 0.04em;
  color: var(--ink-muted); margin-bottom: 8px;
}
.v2-pc-detail {
  font-family: var(--v2-serif);  font-size: var(--ts-small); line-height: 1.6; color: var(--ink);
  margin: 0; max-width: 78ch;
}

/* ---- Side-by-side precedent comparison (curated, Paces-style) ---- */
.v2-pc-compare { font-family: var(--v2-sans); }
.v2-pc-compare-intro { margin: 0 0 18px; max-width: 78ch; }
.v2-pc-compare-takeaway {
  margin: 18px 0 0; padding-top: 16px;
  border-top: 1px solid var(--rule);
  max-width: 78ch;
}
.v2-pc-compare-table {
  width: 100%; border-collapse: collapse;
  table-layout: fixed;
  font-family: var(--v2-sans); font-size: 12px; line-height: 1.45;
  color: var(--ink-body);
}
.v2-pc-compare-table thead th {
  text-align: left; vertical-align: bottom;
  font-family: var(--v2-mono); font-size: 9px;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-muted); font-weight: 600;
  padding: 0 8px 8px; border-bottom: 1px solid var(--ink);
}
.v2-pc-compare-table tbody td {
  padding: 12px 8px; vertical-align: top;
  border-bottom: 1px solid var(--rule);
  color: var(--ink-body);
  word-wrap: break-word; overflow-wrap: break-word;
}
.v2-pc-compare-table tbody tr:last-child td { border-bottom: none; }
.v2-pc-row-label {
  font-family: var(--v2-serif); font-size: 14px;
  font-weight: 600; color: var(--ink); line-height: 1.2;
}
.v2-pc-row-sub {
  font-family: var(--v2-mono); font-size: 9px;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--ink-muted); margin-top: 4px;
}
.v2-pc-row-context {
  font-family: var(--v2-serif); font-style: italic;
  font-size: 12px; line-height: 1.4;
  color: var(--ink-muted); margin-top: 6px;
}
.v2-pc-row-inaoi td { background: rgba(181, 71, 43, 0.05); }
.v2-pc-row-inaoi .v2-pc-row-label { color: var(--ink); }
.v2-pc-row-inaoi .v2-pc-row-sub { color: var(--accent); font-weight: 600; }
.v2-pc-row-emphasis td {
  background: rgba(181, 71, 43, 0.10);
  border-top: 2px solid var(--accent);
  border-bottom-color: var(--accent);
}
.v2-pc-row-emphasis .v2-pc-row-label { color: var(--accent); }
.v2-pc-row-emphasis .v2-pc-row-sub { color: var(--accent); font-weight: 600; }
.v2-pc-row-emphasis .v2-pc-row-context { color: var(--ink); font-style: normal; font-weight: 500; }
.v2-pc-row-emphasis .v2-pc-col-density { color: var(--accent); }

@media print {
  .v2-root { background: white !important; padding: 0 !important; }
  .v2-toolbar { display: none !important; }
  .v2-sheet { box-shadow: none !important; width: 100% !important; }
  .v2-dd-strip { break-inside: avoid; }
  .v2-dd-row { break-inside: avoid; }
  .v2-dd-category .v2-section-head { break-after: avoid; }
  .v2-map-explore { display: none !important; }
}

@media (max-width: 720px) {
  .v2-root { padding: 8px 0 40px; }
  .v2-toolbar { width: 100%; padding: 12px 16px; }
  .v2-sheet { width: 100%; }
  .v2-sheet::before { left: 24px; }
  .v2-dateline { padding: 18px 24px 12px; flex-direction: column; gap: 6px; align-items: flex-start; font-size: var(--ts-micro); }
  .v2-masthead { padding: 32px 24px 20px; }
  .v2-masthead h1 { font-size: var(--ts-stat); }
  .v2-subhead { font-size: var(--ts-body); }
  .v2-qa { grid-template-columns: 1fr; }
  .v2-numbers { grid-template-columns: repeat(2, 1fr); margin: 0 24px; }
  .v2-num { border-bottom: 1px solid var(--rule); }
  .v2-num:nth-child(3), .v2-num:nth-child(4) { border-bottom: none; }
  .v2-num-hero { grid-column: 1 / -1; border-right: none; }
  .v2-num-hero .v2-value { font-size: 44px; }
  .v2-sheet section { padding: 0 24px; }
  .v2-section-head { flex-wrap: wrap; gap: 6px; padding-bottom: 8px; }
  .v2-section-head h2 { margin: 0; font-size: var(--ts-heading); flex-basis: 100%; }
  .v2-meta { flex-basis: 100%; }
  .v2-precedent { grid-template-columns: 1fr; row-gap: 24px; }
  .v2-gates { grid-template-columns: 1fr; }
  .v2-schematic { grid-template-columns: 1fr; }
  .v2-infra-grid { grid-template-columns: 1fr; }
  .v2-epist { grid-template-columns: repeat(2, 1fr); row-gap: 24px; }
  .v2-decision { padding: 28px 24px !important; }
  .v2-footer { padding: 24px; grid-template-columns: 1fr; }
  .v2-sources { text-align: left; }
  .v2-kill td:first-child { width: auto; }
  .v2-kill { font-size: var(--ts-small); }
  .v2-dd-header { padding: 32px 24px 0; }
  .v2-dd-strip { grid-template-columns: repeat(6, 1fr); margin: 20px 24px 0; }
  .v2-dd-body { padding: 0 24px; }
  .v2-dd-row { grid-template-columns: 1fr; gap: 4px; }
  .v2-dd-category { margin-top: 32px; }
  .v2-zone-callout { flex-direction: column; align-items: stretch; }
  .v2-zone-link { align-self: flex-start; }
  .v2-supply-hero[data-cells="3"] { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .v2-supply-title { font-size: var(--ts-subhead); }
  .v2-site-intel { margin: 24px 24px 0; padding: 20px 22px; }
  .v2-pc-stat-row { grid-template-columns: repeat(2, 1fr); }
}
`;
